import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/utils/types';
import ServicePlanBillingPolicyFormModal from 'src/features/servicePlanBillingPolicies/components/forms/ServicePlanBillingPolicyFormModal';
import { LIST_SERVICE_PLAN_BILLING_POLICIES_RESOURCE } from 'src/features/servicePlanBillingPolicies/api/useListServicePlanBillingPolicies';
import { billingPoliciesMessages } from 'src/features/servicePlanBillingPolicies/utils/messages';
import { useParams } from 'react-router-dom';
import { SERVICE_PLAN_BILLING_POLICY_RESOURCE } from 'src/features/servicePlanBillingPolicies/api/useGetServicePlanBillingPolicy';
import { usePatchServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/api/usePatchServicePlanBillingPolicy';

interface UpdateServicePlanBillingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanBillingPolicy: ServicePlanBillingPolicy;
    children?: React.ReactNode;
}

function UpdateServicePlanBillingPolicyFormModal<TData>({
    className,
    semantics,
    children,
    selectedServicePlanBillingPolicy,
}: UpdateServicePlanBillingPolicyFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { servicePlanItemId = '' } = useParams();

    const { mutate, isPending } = usePatchServicePlanBillingPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_BILLING_POLICIES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_BILLING_POLICY_RESOURCE] });
            toast({
                title: formatMessage(billingPoliciesMessages.updateServicePlanBillingPolicySuccess),
                description: formatMessage(billingPoliciesMessages.updateServicePlanBillingPolicySuccessDescription),
            });
        },
    });

    return (
        <ServicePlanBillingPolicyFormModal
            selectedServicePlanBillingPolicy={selectedServicePlanBillingPolicy}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate(
                    { ...values, servicePlanItemId, billingPolicyId: selectedServicePlanBillingPolicy.id },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(billingPoliciesMessages.editServicePlanBillingPolicySubtitle)}
        >
            {children}
        </ServicePlanBillingPolicyFormModal>
    );
}

export default UpdateServicePlanBillingPolicyFormModal;
